import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "./vehiclePricing";

// Placeholder visuel en attendant la vraie photo Mercedes Sprinter VIP
// (à déposer dans app/assets/images/reservation/, cf. CLASS_SPRINTER).
import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const minibusVip: ChauffeurPage = {
  slug: "minibus-vip",
  title: "Minibus VIP avec chauffeur — transport de groupe",
  subtitle:
    "Mercedes Sprinter VIP jusqu'à 16 passagers avec chauffeur sur la Côte d'Azur.",
  keywords: [
    "minibus VIP avec chauffeur",
    "transport de groupe Cannes",
    "navette aéroport groupe Nice",
    "van VIP Côte d'Azur",
  ],
  hero: {
    title:
      "Minibus VIP avec chauffeur — jusqu'à 16 passagers sur la Côte d'Azur",
    subtitle:
      "Mercedes Sprinter VIP : voyagez groupés sans rien sacrifier au confort. Transferts aéroport, événements, séminaires et mariages.",
    keywords: ["minibus VIP", "transport de groupe", "Sprinter chauffeur"],
    imageSrc: heroImage.src,
    imageAlt: "Minibus VIP Mercedes Sprinter avec chauffeur sur la Côte d'Azur",
    badges: ["Jusqu'à 16 passagers", "14 bagages", "Chauffeur dédié"],
  },
  benefits: {
    heading: "Pourquoi le minibus VIP KS Rent Services",
    items: [
      {
        title: "Voyagez groupés",
        description:
          "Tout le groupe dans un seul véhicule : fini les convois éclatés sur plusieurs voitures, vous restez ensemble du départ à l'arrivée.",
      },
      {
        title: "Espace & bagages",
        description:
          "Jusqu'à 16 passagers et 14 bagages : idéal pour les transferts aéroport, les équipages de yacht et les séjours en groupe.",
      },
      {
        title: "Confort VIP",
        description:
          "Sellerie soignée, climatisation, prises de recharge et large espace : le Sprinter en configuration VIP, pas en version navette standard.",
      },
      {
        title: "Chauffeur dédié",
        description:
          "Un seul interlocuteur, un chauffeur ponctuel et l'accueil personnalisé d'un service avec chauffeur haut de gamme.",
      },
    ],
  },
  services: {
    heading: "Prestations de transport de groupe",
    services: [
      {
        title: "Transferts aéroport en groupe",
        text: "Aéroport de Nice, gares et héliport de Monaco : tout le groupe et ses bagages en un seul trajet.",
      },
      {
        title: "Événements & séminaires",
        text: "Congrès, festival, soirées d'entreprise et incentives : navettes coordonnées pour vos équipes et invités.",
      },
      {
        title: "Mariages & familles",
        text: "Cortèges, transferts d'invités et déplacements familiaux, sereins et ponctuels.",
      },
      {
        title: "Mise à disposition",
        text: "Minibus VIP et chauffeur à l'heure ou à la journée, avec le même interlocuteur tout au long de votre programme.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Le groupe ensemble, l'expérience premium",
    subheading:
      "La capacité d'un minibus, le standing d'un service avec chauffeur privé.",
    paragraphs: [
      "Le Mercedes Sprinter VIP réunit jusqu'à 16 personnes dans un même véhicule confortable, sans diviser votre groupe sur plusieurs voitures ni multiplier les chauffeurs.",
      "Chaque déplacement est préparé avec vous : horaires, points de prise en charge et itinéraires sont coordonnés pour que tout le monde arrive ensemble et à l'heure.",
      "Vous gardez un interlocuteur unique, des forfaits aéroport transparents et la flexibilité d'ajuster votre programme à la dernière minute.",
    ],
    bullets: [
      "Jusqu'à 16 passagers et 14 bagages",
      "Forfaits aéroport transparents",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment réserver votre minibus VIP",
    steps: [
      "Indiquez votre trajet, vos horaires et le nombre de passagers.",
      "Recevez un forfait clair ou une estimation au kilomètre.",
      "Affectation du Sprinter VIP et de votre chauffeur dédié.",
      "Prise en charge groupée, ponctuelle et confortable.",
    ],
  },
  faq: [
    {
      q: "Combien de passagers le minibus VIP accueille-t-il ?",
      a: "Le Mercedes Sprinter VIP transporte jusqu'à 16 passagers et 14 bagages, idéal pour les groupes, les familles et les équipages.",
    },
    {
      q: "Pour quels événements le proposez-vous ?",
      a: "Congrès, festival, séminaires, soirées d'entreprise, mariages et transferts de groupe : nous coordonnons les navettes pour vos équipes et vos invités.",
    },
    {
      q: "Assurez-vous les transferts aéroport en groupe ?",
      a: "Oui, depuis et vers l'aéroport de Nice notamment, avec des forfaits transparents pour tout le groupe et ses bagages en un seul trajet.",
    },
    vehiclePriceFaq("fr", "CLASS_SPRINTER", "d'un minibus VIP avec chauffeur"),
    {
      q: "Comment réserver ?",
      a: "Indiquez-nous votre trajet, vos horaires et le nombre de passagers : nous confirmons rapidement le Sprinter VIP et votre chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Tous les transferts de la Côte d’Azur",
      href: "/chauffeur/transferts",
    },
    { label: "Chauffeur pour entreprises", href: "/chauffeur/entreprise" },
    { label: "Chauffeur pour événements", href: "/chauffeur/evenements" },
    {
      label: "Voiture de mariage à Cannes",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Un groupe à transporter ?",
    text: "Dites-nous votre trajet et votre effectif — devis rapide pour votre minibus VIP avec chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis groupe",
  },
  seo: {
    ...vehicleSeo(
      "fr",
      "CLASS_SPRINTER",
      "Minibus VIP avec chauffeur, 16 places",
      "Sprinter VIP 16 places avec chauffeur",
    ),
    canonical: "/chauffeur/minibus-vip",
  },
};

export default minibusVip;
